"use client"

import {
    Avatar,
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownPopover,
    DropdownSection,
    DropdownTrigger,
} from "@heroui/react";
import { useUser } from "./dpswr";
import { useContext, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Bars3Icon } from "@heroicons/react/24/solid";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline"
import { ServiceUser } from "@/validated-servertypes";
import DeleteAccountModal from "./delete-account-modal";
import { useStytch } from "@stytch/nextjs";
import { useSWRConfig } from "swr";
import { disableKeyOnDisabledKeys } from "./client-utils";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LocaleContext } from "@/app/[lang]/intlprovider";
import { LDClient, useLDClient } from "launchdarkly-react-client-sdk";
import { identifyServiceUserToLaunchDarkly } from "@/app/[lang]/idld"

export default function DonorPortalUserNav () {

    const [signupActive, setSignupActive] = useState<boolean>( true );
    const [loginActive, setLoginActive] = useState<boolean>( true );
    const [userSignedIn, setUserSignedIn] = useState<boolean>( false );
    const [userIsAdmin, setUserIsAdmin] = useState<boolean>( false );
    const disabledKeysState = useState<(string | number)[]>( [] );

    const {user, isLoading, isAdmin } = useUser();
    const ldc: LDClient | undefined = useLDClient()

    const pathname: string = usePathname();

    const [isOpen, setIsOpen] = useState(false);
    const onOpen = () => setIsOpen(true);
    const onClose = () => setIsOpen(false);
    const onOpenChange = () => setIsOpen(prev => !prev);

    const stytchClient = useStytch();
    const { mutate } = useSWRConfig();
    const router = useRouter();

    const [locale] = useContext( LocaleContext );

    disableKeyOnDisabledKeys( disabledKeysState, locale );

    useEffect( () => {

            if ( !isLoading ) {
                if ( !user ) {
                    // eslint-disable-next-line react-hooks/set-state-in-effect
                    setUserSignedIn( false );

                    if ( pathname.endsWith( "/signup" ) ||
                         pathname.endsWith( "/authenticated-user-signup" ) ||
                         pathname.endsWith( "/login" ) ||
                         pathname.endsWith( "/authenticated-user-login" ) ) {
                        setSignupActive( false );
                        setLoginActive( false );
                    }
                    else {
                        setSignupActive( true );
                        setLoginActive( true );
                    }
                }
                else {
                    setUserSignedIn( true );
                    if ( isAdmin !== undefined ) {
                        setUserIsAdmin( isAdmin );
                    }
                }
            }

        }, [pathname, user, isLoading, isOpen, isAdmin]
    );

    useEffect( () => {
        if ( userSignedIn ) {
            identifyServiceUserToLaunchDarkly( user as ServiceUser, ldc as LDClient ).then(() => {}).catch(() => {});
        }
    }, [userSignedIn, user, ldc])

    if ( userSignedIn ) {

        return(
            <>
                <div className="flex flex-row-reverse">
                    <div>
                        <Dropdown>
                            <DropdownTrigger>
                                <Button
                                    className="bg-kexp-light-primary-900 text-white"
                                    variant="ghost"
                                    size="lg"
                                >
                                    <Bars3Icon
                                        className="w-4 md:w-6"
                                    />
                                    <Avatar size="sm">
                                        <Avatar.Fallback className="bg-kexp-light-primary-300">{user?.name?.slice( 0, 1 ).toUpperCase()}</Avatar.Fallback>
                                    </Avatar>
                                </Button>
                            </DropdownTrigger>
                            <DropdownPopover className="bg-kexp-light-layout-foreground border-kexp-light-layout-foreground">
                            <DropdownMenu
                                aria-label="User menu"
                                className="text-kexp-light-layout-background"
                                disabledKeys={disabledKeysState[0]}
                            >
                                <DropdownSection>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        key="recentgiving"
                                        textValue={t`Recent Giving`}
                                        onPress={() => router.push( "/" + locale + "/recent-giving" )}
                                    >
                                        <Trans>Recent Giving</Trans>
                                    </DropdownItem>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        key="billing"
                                        textValue={t`Billing`}
                                        onPress={() => router.push( "/" + locale + "/billing" )}
                                    >
                                        <Trans>Billing</Trans>
                                    </DropdownItem>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        key="accountsettings"
                                        textValue={t`Account Settings`}
                                        onPress={() => router.push( "/" + locale + "/account-settings" )}
                                    >
                                        <Trans>Account Settings</Trans>
                                    </DropdownItem>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        key="donorsupport"
                                        textValue={t`Support`}
                                        onPress={() => router.push( "/" + locale + "/support" )}
                                    >
                                        <Trans>Support</Trans>
                                    </DropdownItem>
                                    { userIsAdmin ? (<DropdownItem
                                            className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                            key="adminfinddonor"
                                            textValue={t`Admin: Find Donor`}
                                            onPress={() => router.push( "/" + locale + "/admin-find-donor" )}
                                        >
                                            <Trans>Admin: Find Donor</Trans>
                                        </DropdownItem>) : (<DropdownItem
                                            key="placeholder"
                                            className="invisible"
                                        >
                                        </DropdownItem>)
                                    }
                                </DropdownSection>
                                <DropdownSection>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        href={"https://www.kexp.org/terms-and-conditions/"}
                                        key="termsofservice"
                                        textValue={t`Terms of Service`}
                                    >
                                        <Trans>Terms of Service</Trans>
                                    </DropdownItem>
                                    <DropdownItem
                                        className="text-kexp-light-layout-background data-[hovered]:text-kexp-light-layout-foreground"
                                        href={"https://www.kexp.org/privacy-policy/"}
                                        key="privacypolicy"
                                        textValue={t`Privacy Policy`}
                                    >
                                        <Trans>Privacy Policy</Trans>
                                    </DropdownItem>
                                    <DropdownItem
                                        key="logout"
                                        textValue={t`Logout`}
                                        className="text-kexp-light-primary-200 data-[hovered]:text-kexp-light-layout-foreground"
                                        onPress={
                                            // NOTENOTE: setTimeout use is a workaround for React Aria issue
                                            //           https://github.com/adobe/react-spectrum/issues/4514
                                            () => setTimeout(
                                                // NOTENOTE: Need to use await
                                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                                async () => {
                                                    await stytchClient.session.revoke();
                                                    await mutate( ["getCurrentUser", undefined] );
                                                    // BUGBUG: Need to identify anonymous user to LaunchDarkly
                                                    router.push( "/" + locale );
                                                },
                                                500
                                            )
                                        }
                                    >
                                        <ArrowRightOnRectangleIcon className="h-4 w-4 inline mr-1"/><Trans>Logout</Trans>
                                    </DropdownItem>
                                </DropdownSection>
                            </DropdownMenu>
                            </DropdownPopover>
                        </Dropdown>
                    </div>
                </div>
                <DeleteAccountModal
                    isOpen={isOpen}
                    onOpen={onOpen}
                    onClose={onClose}
                    onOpenChange={onOpenChange}
                    locale={locale}
                />
            </>
        );
    }

    return(
        <div className="flex flex-row-reverse gap-2">
            <div>
                <Button
                    variant="ghost"
                    size="lg"
                    className="rounded-full sm:hidden bg-kexp-light-default-100 text-kexp-light-layout-foreground hover:opacity-80 transition-opacity"
                    isDisabled={!loginActive}
                    onPress={() => router.push("/" + locale + "/login")}
                >
                    <Trans>Login</Trans>
                </Button>
                <Button
                    variant="ghost"
                    size="lg"
                    className="rounded-full hidden sm:flex bg-kexp-light-default-100 text-kexp-light-layout-foreground hover:opacity-80 transition-opacity"
                    isDisabled={!loginActive}
                    onPress={() => router.push("/" + locale + "/login")}
                >
                    <Trans>Login</Trans>
                </Button>
            </div>
            <div>
                <Button
                    variant="primary"
                    size="lg"
                    className="rounded-full sm:hidden transition-opacity"
                    isDisabled={!signupActive}
                    onPress={() => router.push("/" + locale + "/signup")}
                >
                    <Trans>Sign up</Trans>
                </Button>
                <Button
                    variant="primary"
                    size="lg"
                    className="rounded-full hidden sm:flex transition-opacity"
                    isDisabled={!signupActive}
                    onPress={() => router.push("/" + locale + "/signup")}
                >
                    <Trans>Sign up</Trans>
                </Button>
            </div>
        </div>
    );
}